import { Component, OnInit, signal, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';

import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { NotificationService } from '@core/services/notification.service';
import { CampaignsService, AgentsService, TelecomService } from '@generated/services';
import { DeleteConfirmationComponent } from '@shared/components/delete-confirmation/delete-confirmation';
import { AgentSummary } from '@generated/models/agent-summary';
import { PhoneNumberAgentResult } from '@generated/models/phone-number-agent-result';
import { CampaignStatus } from '@generated/models/campaign-status';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';
import { AssistantEditableDirective } from '@shared/directives/assistant-editable.directive';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';
import { CAMPAIGN_FIELDS } from '@core/assistant/assistant-catalog';

@Component({
  selector: 'app-outbound-campaign-edit',
  imports: [
    AssistantSurfaceDirective,
    AssistantEditableDirective,
    AssistantActionDirective,
    FormsModule,
    RouterModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    CardModule,
    ProgressSpinnerModule,
    ConfirmDialogModule,
    DeleteConfirmationComponent
],
  providers: [ConfirmationService],
  templateUrl: './outbound-campaign-edit.html',
  styleUrl: './outbound-campaign-edit.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OutboundCampaignEdit implements OnInit {
  /** Идентификаторы полей для агента: опечатку ловит компилятор. */
  protected readonly F = CAMPAIGN_FIELDS;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);
  private notificationService = inject(NotificationService);
  private campaignsService = inject(CampaignsService);
  private agentsService = inject(AgentsService);
  private telecomService = inject(TelecomService);
  private confirmationService = inject(ConfirmationService);

  campaignId: string | null = null;
  campaignName = '';
  selectedAgentId: string | null = null;
  selectedPhoneNumberAgentId: string | null = null;
  agents: AgentSummary[] = [];
  agentOptions: { label: string; value: string }[] = [];
  phoneNumberAgents: PhoneNumberAgentResult[] = [];
  phoneNumberAgentOptions: { label: string; value: string }[] = [];
  itemsCount = 0;
  campaignStatus: CampaignStatus | null = null;
  isLoading = signal(false);
  isSaving = signal(false);
  isStarting = signal(false);
  isStopping = signal(false);
  showDeleteDialog = signal(false);
  isDeleting = signal(false);
  hasUnsavedChanges = signal(false);

  private originalCampaignName = '';
  private originalAgentId: string | null = null;
  private originalPhoneNumberAgentId: string | null = null;

  readonly deleteTitle = $localize`:@@campaignEdit.delete.title:Delete Campaign`;
  readonly deleteSubtitle = $localize`:@@campaignEdit.delete.subtitle:Are you sure you want to delete this campaign? This action cannot be undone.`;

  ngOnInit() {
    this.campaignId = this.route.snapshot.paramMap.get('id');
    this.loadAgents();
    this.loadPhoneNumberAgents();
    this.loadCampaign();
  }

  private loadAgents() {
    this.agentsService.apiAgentsGet().subscribe({
      next: (result) => {
        const unnamed = $localize`:@@conversations.unnamedAgent:Unnamed Agent`;
        this.agents = result.agents || [];
        this.agentOptions = this.agents.map(agent => ({
          label: agent.title || unnamed,
          value: agent.id!
        }));
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load agents:', error);
        this.notificationService.showError($localize`:@@conversations.notify.agentsFail:Failed to load agents`);
      }
    });
  }

  private loadPhoneNumberAgents() {
    this.telecomService.apiTelecomAccountsGet().subscribe({
      next: (accounts) => {
        const list = accounts || [];
        this.phoneNumberAgents = list.flatMap(a => a.phone_number_agents || []);
        this.phoneNumberAgentOptions = list.flatMap(account =>
          (account.phone_number_agents || []).map(pna => ({
            label: this.formatPhoneNumberAgentLabel(pna, account.provider),
            value: pna.id!
          }))
        );
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load phone number agents:', error);
        this.notificationService.showError($localize`:@@campaignEdit.notify.phoneNumbersFail:Failed to load phone numbers`);
      }
    });
  }

  private formatPhoneNumberAgentLabel(pna: PhoneNumberAgentResult, provider: string | undefined): string {
    const phone = pna.phone_number || $localize`:@@campaignEdit.noNumber:No number`;
    const providerLabel = this.providerLabel(provider);
    const head = pna.label ? `(${pna.label}) ${phone}` : phone;
    return providerLabel ? `${head} — ${providerLabel}` : head;
  }

  private providerLabel(provider: string | undefined): string {
    switch (provider) {
      case 'Custom': return $localize`:@@telecomProvider.custom:Custom SIP`;
      case 'Twilio': return 'Twilio';
      case 'Voximplant': return 'Voximplant';
      default: return provider ?? '';
    }
  }

  loadCampaign() {
    if (!this.campaignId) return;

    this.isLoading.set(true);

    this.campaignsService.apiCampaignsCampaignIdGet({ campaignId: this.campaignId }).subscribe({
      next: (campaign) => {
        this.campaignName = campaign.title || '';
        this.selectedAgentId = campaign.agent_id || null;
        this.selectedPhoneNumberAgentId = campaign.phone_number_agent_id || null;
        this.itemsCount = campaign.items_count ?? 0;
        this.campaignStatus = campaign.status ?? null;
        this.originalCampaignName = this.campaignName;
        this.originalAgentId = this.selectedAgentId;
        this.originalPhoneNumberAgentId = this.selectedPhoneNumberAgentId;
        this.isLoading.set(false);
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load campaign:', error);
        this.notificationService.showError($localize`:@@campaignEdit.notify.loadFail:Failed to load campaign`);
        this.isLoading.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  onFieldChange() {
    this.hasUnsavedChanges.set(
      this.campaignName !== this.originalCampaignName ||
      this.selectedAgentId !== this.originalAgentId ||
      this.selectedPhoneNumberAgentId !== this.originalPhoneNumberAgentId
    );
  }

  canNavigateToItems(): boolean {
    return !!this.selectedAgentId && !this.hasUnsavedChanges();
  }

  onSave() {
    if (!this.campaignName.trim()) {
      this.notificationService.showError($localize`:@@campaignEdit.notify.nameRequired:Please enter a campaign name`);
      return;
    }

    if (!this.selectedAgentId) {
      this.notificationService.showError($localize`:@@campaignCreate.notify.agentRequired:Please select an agent`);
      return;
    }

    if (!this.selectedPhoneNumberAgentId) {
      this.notificationService.showError($localize`:@@campaignEdit.notify.phoneRequired:Please select a phone number`);
      return;
    }

    this.isSaving.set(true);

    this.campaignsService.apiCampaignsCampaignIdPut({
      campaignId: this.campaignId!,
      body: {
        campaign_id: this.campaignId!,
        title: this.campaignName,
        agent_id: this.selectedAgentId,
        phone_number_agent_id: this.selectedPhoneNumberAgentId
      }
    }).subscribe({
      next: () => {
        this.notificationService.showSuccess($localize`:@@campaignEdit.notify.updated:Campaign updated`);
        this.originalCampaignName = this.campaignName;
        this.originalAgentId = this.selectedAgentId;
        this.originalPhoneNumberAgentId = this.selectedPhoneNumberAgentId;
        this.hasUnsavedChanges.set(false);
        this.isSaving.set(false);
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to update campaign:', error);
        this.notificationService.showError($localize`:@@campaignEdit.notify.updateFail:Failed to update campaign`);
        this.isSaving.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  onCancel() {
    this.router.navigate(['/outbound-campaigns']);
  }

  canStart(): boolean {
    return this.campaignStatus === 'Initial' && !this.hasUnsavedChanges();
  }

  /** Остановить можно только то, что реально идёт. */
  canStop(): boolean {
    return this.campaignStatus === 'Processing';
  }

  /** Продолжить — снять остановку и добрать оставшиеся номера. */
  canResume(): boolean {
    return this.campaignStatus === 'Stopped' && !this.hasUnsavedChanges();
  }

  /**
   * Начать заново — только для отработавших. Сброс реестра выводится на бэке
   * из статуса кампании, отдельного намерения отсюда не передаётся: у
   * остановленной его и быть не может, она всегда продолжается с места.
   */
  canRestart(): boolean {
    return (this.campaignStatus === 'Completed' || this.campaignStatus === 'Failed')
           && !this.hasUnsavedChanges();
  }

  onStartClick() {
    this.confirmationService.confirm({
      message: $localize`:@@campaignEdit.start.confirmMessage:Are you sure you want to start this campaign? The campaign will begin processing all items.`,
      header: $localize`:@@campaignEdit.startCampaign:Start Campaign`,
      icon: 'pi pi-play',
      accept: () => {
        this.startCampaign();
      }
    });
  }

  onStopClick() {
    this.confirmationService.confirm({
      message: $localize`:@@campaignEdit.stop.confirmMessage:Stop this campaign? The call in progress will finish, but no further numbers will be dialled.`,
      header: $localize`:@@campaignEdit.stopCampaign:Stop Campaign`,
      icon: 'pi pi-pause',
      accept: () => this.stopCampaign()
    });
  }

  onResumeClick() {
    this.startCampaign();
  }

  onRestartClick() {
    this.confirmationService.confirm({
      message: $localize`:@@campaignEdit.restart.confirmMessage:Start this campaign over? Every contact will be dialled again, including those already called.`,
      header: $localize`:@@campaignEdit.restartCampaign:Start Over`,
      icon: 'pi pi-replay',
      accept: () => this.startCampaign()
    });
  }

  private stopCampaign() {
    if (!this.campaignId) return;

    this.isStopping.set(true);

    this.campaignsService.apiCampaignsCampaignIdStopPatch({ campaignId: this.campaignId }).subscribe({
      next: () => {
        this.notificationService.showSuccess($localize`:@@campaignEdit.notify.stopped:Campaign stopped`);
        this.isStopping.set(false);
        this.campaignStatus = 'Stopped';
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to stop campaign:', error);
        this.notificationService.showError($localize`:@@campaignEdit.notify.stopFail:Failed to stop campaign`);
        this.isStopping.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  private startCampaign() {
    if (!this.campaignId) return;

    this.isStarting.set(true);

    this.campaignsService.apiCampaignsCampaignIdStartPost({ campaignId: this.campaignId }).subscribe({
      next: () => {
        this.notificationService.showSuccess($localize`:@@campaignEdit.notify.started:Campaign started`);
        this.isStarting.set(false);
        this.campaignStatus = 'Processing';
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to start campaign:', error);
        this.notificationService.showError($localize`:@@campaignEdit.notify.startFail:Failed to start campaign`);
        this.isStarting.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  onDeleteClick() {
    this.showDeleteDialog.set(true);
  }

  onDeleteConfirm() {
    if (!this.campaignId) return;

    this.isDeleting.set(true);

    this.campaignsService.apiCampaignsCampaignIdDelete({ campaignId: this.campaignId }).subscribe({
      next: () => {
        this.notificationService.showSuccess($localize`:@@campaignEdit.notify.deleted:Campaign deleted`);
        this.showDeleteDialog.set(false);
        this.router.navigate(['/outbound-campaigns']);
      },
      error: (error) => {
        console.error('Failed to delete campaign:', error);
        this.notificationService.showError($localize`:@@campaignEdit.notify.deleteFail:Failed to delete campaign`);
        this.isDeleting.set(false);
        this.cdr.markForCheck();
      }
    });
  }
}
